import React, { useState, useContext, useEffect, useCallback, useRef } from "react";
import { OnlineStatusContext } from "./OnlineStatusContext";
import { IOnlineStatusProviderProps } from "./OnlineStatusInterfaces";
import { BackendConnectionEntity } from "entities/BackendConnection/BackendConnectionEntity";
import { initialState } from "./OnlineStatusContext";
import { useLocation } from "react-router-dom";
import { CHECKBACKENDCONNECTIONINTERVAL, MachineCenterLogTriggersEnum } from "constants/constants";
import useMachineCenterLogs from "hooks/useMachineCenterLogs";
import {
    getCurrentFailureDetailsStep,
    getCurrentStartBelowTriggerDowntimeCount,
    getCurrentStartDowntime,
    isPlcDisconnected,
    isUptimeActive,
} from "utilities/storageUtilities";
import { HMICachedDataContext } from "contexts/HMICachedDataContext/HMICachedDataContext";

export const OnlineStatusProvider: React.FC<IOnlineStatusProviderProps> = ({ children }) => {
    const [onlineStatus, setOnlineStatus] = useState<boolean>(initialState.onlineStatus!);
    const location = useLocation();
    const { triggerMachineCenterLogs } = useMachineCenterLogs();
    const prevOnlineStatusRef = useRef<boolean | null>(null);
    const uptime = isUptimeActive();
    const currentFailureDetailsStepFromLocalStorage = getCurrentFailureDetailsStep();
    const currentStartDowntimeBelowTrigger = getCurrentStartBelowTriggerDowntimeCount();
    const { store } = useContext(HMICachedDataContext);

    const currentStartDowntime = currentStartDowntimeBelowTrigger
        ? currentStartDowntimeBelowTrigger
        : getCurrentStartDowntime();

    const getConnectionStatus = async () => {
        const connectionToBackend = await BackendConnectionEntity.GetBackendConnection();
        const isOnline = !!(connectionToBackend && connectionToBackend === "alive");
        setOnlineStatus(isOnline);
        return isOnline;
    };

    const updateOnlineStatus = useCallback(async () => {
        if (location.pathname.toLowerCase().includes("hmi")) {
            return await getConnectionStatus();
        }
        return true;
    }, [location]);

    useEffect(() => {
        getConnectionStatus();
        const interval = setInterval(async () => {
            const isOnline = await updateOnlineStatus();
            setOnlineStatus(isOnline);

            if (prevOnlineStatusRef.current === false && isOnline === true) {
                if (process.env.REACT_APP_LOGS_ENABLED) {
                    const plcDisconnected = isPlcDisconnected();
                    const message = {
                        machine_status: uptime,
                        currentFailureDetailsStepFromLocalStorage: currentFailureDetailsStepFromLocalStorage ?? null,
                        plcDisconnected: plcDisconnected,
                        currentStartDowntime: currentStartDowntime ?? null,
                        store: store,
                    };
                    triggerMachineCenterLogs(
                        MachineCenterLogTriggersEnum.AT_INTERNET_CONNECTION_RESTORED,
                        JSON.stringify(message),
                        new Date().toISOString(),
                    );
                }
            }

            prevOnlineStatusRef.current = isOnline;
        }, CHECKBACKENDCONNECTIONINTERVAL);

        return () => clearInterval(interval);
    }, [
        updateOnlineStatus,
        currentFailureDetailsStepFromLocalStorage,
        currentStartDowntime,
        triggerMachineCenterLogs,
        uptime,
        store,
    ]);

    const providerValues = {
        onlineStatus,
        updateOnlineStatus,
    };

    return <OnlineStatusContext.Provider value={providerValues}>{children}</OnlineStatusContext.Provider>;
};

export const useOnlineStatus = () => {
    const store = useContext(OnlineStatusContext);
    return store;
};
